import { User } from "@/types/auth";
import authService, { setUserStrategiesCache } from "@/services/authService";
import { ApiError } from "@/lib/apiClient";
import { queryClient } from "@/lib/queryClient";
import {
  getOnboardingProgress,
  getSelectedStrategyId,
  setOnboardingProgress,
} from "./onboardingStorage";

export function isProfileComplete(user: User): boolean {
  if (!user) return false;
  return !!(
    user.name &&
    user.email &&
    user.phone &&
    user.dob &&
    user.panCard
  );
}

/** True after the user has finished confirm + document signing on /confirm. */
export function hasCompletedConfirmStep(
  progress: string | null = getOnboardingProgress()
): boolean {
  return (
    progress === "profile_completed" ||
    progress === "subscription_completed" ||
    progress === "credentials_completed" ||
    progress === "completed"
  );
}

export type OnboardingStatusSnapshot = {
  userFound?: boolean;
  profileComplete?: boolean;
  hasStrategySubscription: boolean;
  hasSignedAgreement?: boolean;
  hasBrokerCredentials: boolean;
};


export const onboardingStatusKey = (uid?: string) =>
  ["onboarding", "status", uid] as const;

export function clearOnboardingStatusCache(): void {
  queryClient.removeQueries({ queryKey: ["onboarding", "status"] });
}

function localSubscriptionHint(): boolean {
  const progress = getOnboardingProgress();
  return (
    progress === "credentials_completed" ||
    progress === "completed" ||
    progress === "subscription_completed"
  );
}

function isMainAppRoute(pathname: string): boolean {
  return (
    pathname === "/dashboard" ||
    pathname === "/strategies" ||
    pathname === "/orders-trades" ||
    pathname === "/report" ||
    pathname.startsWith("/performance") ||
    pathname.startsWith("/portfolio")
  );
}

export function canSkipOnboardingGuard(pathname: string): boolean {
  return (
    getOnboardingProgress() === "completed" && isMainAppRoute(pathname)
  );
}

/**
 * Network + mapping for the onboarding status. Used as the TanStack Query
 * `queryFn` so every caller (the React hook and the imperative guard helpers)
 * shares one cache entry and one in-flight request per uid.
 */
export async function queryOnboardingStatus(
  user: User
): Promise<OnboardingStatusSnapshot> {
  try {
    const response = await authService.getOnboardingStatus(user.firebase_uid);
    const data: OnboardingStatusSnapshot = {
      userFound: response.user_found,
      profileComplete: response.profile_complete,
      hasStrategySubscription:
        response.has_strategy_subscription || localSubscriptionHint(),
      hasSignedAgreement:
        response.has_signed_agreement !== undefined ? response.has_signed_agreement : true,
      hasBrokerCredentials: response.has_broker_credentials,
    };

    if (data.hasBrokerCredentials && data.userFound !== false) {
      setOnboardingProgress("completed");
    }

    // Seed strategies cache from the merged onboarding response so the
    // /strategies page shows the correct "Already Enrolled" state even when
    // users_profile Strategy_* columns are null (trading-DB-only subscriptions).
    const strategies: string[] = Array.isArray(response.strategies)
      ? response.strategies
      : [];
    if (strategies.length > 0 && user.firebase_uid) {
      setUserStrategiesCache(user.firebase_uid, strategies);
    }

    return data;
  } catch (error) {
    // A 404 means the backend has no record for this user — treat it the same
    // as user_found: false. Do NOT call queryClient.clear() here (inside a
    // queryFn) — that aborts the in-flight query and causes an infinite loop.
    // The caller (AuthProvider / ProtectedRoute) handles logout and cleanup.
    if (error instanceof ApiError && error.status === 404) {
      return {
        userFound: false,
        hasStrategySubscription: false,
        hasBrokerCredentials: false,
      };
    }
    // For other errors (network timeouts, 5xx etc.) fall back to local hints
    // so a transient outage doesn't log the user out.
    return {
      userFound: true,
      hasStrategySubscription: localSubscriptionHint(),
      hasBrokerCredentials: getOnboardingProgress() === "completed",
    };
  }
}

export async function fetchOnboardingStatus(
  user: User,
  forceRefresh = false
): Promise<OnboardingStatusSnapshot> {
  if (forceRefresh) {
    // Evict any cached entry so the query always hits the network.
    // This is used on page-load auth init to detect deleted users.
    queryClient.removeQueries({ queryKey: onboardingStatusKey(user.firebase_uid) });
  }
  return queryClient.fetchQuery({
    queryKey: onboardingStatusKey(user.firebase_uid),
    queryFn: () => queryOnboardingStatus(user),
    staleTime: forceRefresh ? 0 : 5 * 60 * 1000,
  });
}

type OnboardingStep = "confirm" | "subscribe" | "broker";

export function getOnboardingStep(pathname: string): OnboardingStep | null {
  if (pathname === "/confirm") return "confirm";
  if (
    pathname === "/subscribe" ||
    pathname === "/subscription" ||
    pathname === "/strategies" ||
    pathname.startsWith("/strategy/")
  ) {
    return "subscribe";
  }
  if (
    pathname === "/brokers" ||
    pathname === "/broker/connection" ||
    pathname.startsWith("/broker/")
  ) {
    return "broker";
  }
  return null;
}

/** Map saved local progress to the route the user should resume on. */
export function getResumePathFromProgress(
  user: User,
  progress: string | null
): string | null {
  if (!isProfileComplete(user)) {
    return "/confirm";
  }
  switch (progress) {
    case "profile_completed":
      return "/subscribe";
    case "subscription_completed":
    case "credentials_completed":
      return "/broker/connection";
    default:
      return null;
  }
}

/**
 * Synchronous core of the onboarding decision. Identical logic to the previous
 * async `resolveExpectedOnboardingPath`, but takes an already-resolved status
 * snapshot so callers that have the cached status can decide without awaiting.
 */
export function resolveExpectedOnboardingPathSync(
  user: User,
  snapshot: OnboardingStatusSnapshot
): string {
  if (snapshot.userFound === false) {
    return "/login";
  }

  // Step 2: Verify Profile Details (PAN, DOB, Phone, Name)
  if (!snapshot.profileComplete && !isProfileComplete(user)) {
    return "/confirm";
  }

  // Step 3: Product Selection & DocuSeal Agreement
  if (!snapshot.hasStrategySubscription || snapshot.hasSignedAgreement === false) {
    return "/subscribe";
  }

  // Step 4: Broker Credentials / Account Setup
  if (!snapshot.hasBrokerCredentials) {
    return "/broker/connection";
  }

  return "/dashboard";
}

export async function resolveExpectedOnboardingPath(
  user: User,
  status?: OnboardingStatusSnapshot
): Promise<string> {
  const snapshot = status ?? (await fetchOnboardingStatus(user));
  return resolveExpectedOnboardingPathSync(user, snapshot);
}

/**
 * Synchronous core of `getNextOnboardingStep`. Same decisions, but takes the
 * resolved status snapshot so the route guard can derive the redirect target
 * without an async round-trip (kills the render-then-redirect flash).
 */
const stepRank: Record<OnboardingStep, number> = {
  confirm: 0,
  subscribe: 1,
  broker: 2,
};

export function computeNextOnboardingStep(
  user: User | null,
  currentPath: string,
  status: OnboardingStatusSnapshot
): string | null {
  if (!user) return "/login";

  if (status.userFound === false) {
    return "/login";
  }

  if (status.hasBrokerCredentials) {
    setOnboardingProgress("completed");
    if (isOnboardingFlowPath(currentPath)) {
      return "/dashboard";
    }
    return null;
  }

  const expectedPath = resolveExpectedOnboardingPathSync(user, status);
  const currentStep = getOnboardingStep(currentPath);
  const expectedStep = getOnboardingStep(expectedPath);

  if (currentStep && expectedStep) {
    if (stepRank[currentStep] <= stepRank[expectedStep]) {
      return null;
    }
  }

  if (currentPath === expectedPath) {
    return null;
  }

  return expectedPath;
}

export async function getNextOnboardingStep(
  user: User | null,
  currentPath: string
): Promise<string | null> {
  if (!user) return "/login";
  const status = await fetchOnboardingStatus(user);
  return computeNextOnboardingStep(user, currentPath, status);
}

/** Routes that are part of the onboarding wizard (no top navbar). */
export function isOnboardingFlowPath(pathname: string): boolean {
  return (
    pathname === "/confirm" ||
    pathname === "/subscribe" ||
    pathname === "/subscription" ||
    pathname === "/brokers" ||
    pathname === "/broker/connection" ||
    pathname.startsWith("/broker/")
  );
}

function isMainAppPath(pathname: string): boolean {
  return (
    pathname === "/dashboard" ||
    pathname === "/strategies" ||
    pathname === "/orders-trades" ||
    pathname === "/report" ||
    pathname.startsWith("/performance") ||
    pathname.startsWith("/portfolio")
  );
}

export function shouldShowNavbar(
  isAuthenticated: boolean,
  pathname: string
): boolean {
  if (!isAuthenticated) return false;

  if (isOnboardingFlowPath(pathname)) {
    return false;
  }

  if (isMainAppPath(pathname)) {
    return true;
  }

  if (
    (pathname === "/subscribe" || pathname === "/subscription") &&
    getOnboardingProgress() === "completed"
  ) {
    return true;
  }

  return false;
}
